// src/pages/HowItWorks.tsx
import "@/styles/marketing.css";
import MarketingHeader, { APP_STORE_URL } from "@/components/marketing/MarketingHeader";
import MarketingFooter from "@/components/marketing/MarketingFooter";

const STEPS = [
  {
    n: "01",
    title: "Sign up",
    body: "Goals, sports, biometrics. Two minutes, then your free trial starts.",
  },
  {
    n: "02",
    title: "Download the app",
    body: "Everything lives in one place — coach, training, nutrition, progress.",
  },
  {
    n: "03",
    title: "Connect your data",
    body: "Garmin, WHOOP, Apple Health. Oura is on the way. Manual logging works too.",
  },
  {
    n: "04",
    title: "Train off the readout",
    body: "Personalised plans and advice that shift with your actual state, every day.",
  },
];

const COACH_CAN = [
  ["Chat anytime", "Meal plans, training questions, recovery calls, session analysis — answered in seconds, 24/7."],
  ["Read your wearables", "Sleep, HRV, strain and recovery from Garmin and WHOOP feed every answer."],
  ["Log meals from a photo", "Photograph a plate or scan a barcode; the macros land in your log."],
  ["Debrief every workout", "Post-session analysis and a refuelling plan with exact numbers."],
  ["Adjust your day", "Calories and intensity recalculated from recovery and weigh-in data."],
  ["Review your week", "A Sunday summary with trends and what to change next week."],
];

const APP_HAS = [
  ["Program library", "Bodybuilding, powerlifting, Olympic lifting, endurance and more — preset or AI-generated."],
  ["Adaptive nutrition", "AI meal plans matched to your macros, diet style and restrictions."],
  ["One-tap logging", "Workouts and meals logged fast, with barcode scanning and smart suggestions."],
  ["Live equipment tracking", "Concept2, Echo Bike, treadmill and HR straps streaming over Bluetooth."],
  ["Leaderboards & challenges", "Compete with the community; streaks and XP keep the boring weeks honest."],
  ["Progress dashboard", "Training, nutrition and recovery visualised in one place."],
];

const FAQ = [
  {
    q: "How does the AI coach work?",
    a: "You chat with it in the app, like texting a coach. It reads your connected devices (Garmin, WHOOP), your training log and your goals before answering — so the advice accounts for how you've actually been sleeping, eating and training.",
  },
  {
    q: "What can I ask it?",
    a: "Anything training-shaped. \"What's my meal plan today?\", \"How should I train given my recovery?\", \"Analyse today's class workout\", \"Write me a 5K PB plan but mind my calf.\" It remembers injuries, PBs and preferences.",
  },
  {
    q: "Do I need wearables?",
    a: "No — everything can be logged manually. But connecting Garmin, WHOOP or Apple Health gives the coach real recovery and activity data, and that's where the advice gets sharp. Oura is coming.",
  },
  {
    q: "Is everything in one app?",
    a: "Yes. Coach, workout logging, training programs, nutrition tracking and the progress dashboard are all in the one app.",
  },
  {
    q: "Is my data private?",
    a: "Yes. We never share or sell your data. Everything is encrypted and you control what you connect. UK-based, ICO-registered.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes — directly in Settings → Billing. No contracts.",
  },
  {
    q: "How much does it cost?",
    a: "£2.99/week after your free trial.",
  },
];

export default function HowItWorks() {
  return (
    <div className="mk">
      <MarketingHeader />

      <main>
        {/* Hero */}
        <section className="border-b" style={{ borderColor: "var(--mk-line)" }}>
          <div className="max-w-screen-xl mx-auto px-5 pt-20 pb-16">
            <div className="mk-label mb-6" style={{ color: "var(--mk-signal)" }}>
              How it works
            </div>
            <h1 className="mk-display max-w-3xl" style={{ fontSize: "clamp(36px, 6vw, 68px)" }}>
              One system.
              <br />
              All your data.
            </h1>
            <p className="mt-7 max-w-xl text-[16px] leading-relaxed" style={{ color: "var(--mk-muted)" }}>
              Input connects your wearables, training and nutrition
              into one system — the full picture of what your body's doing and
              what it needs next. The AI coaching, logging and programming all
              run off that.
            </p>
          </div>
        </section>

        {/* Steps */}
        <section className="border-b" style={{ borderColor: "var(--mk-line)" }}>
          <div className="max-w-screen-xl mx-auto px-5 py-16 md:py-24">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px" style={{ background: "var(--mk-line)" }}>
              {STEPS.map((step) => (
                <div key={step.n} className="p-8" style={{ background: "var(--mk-carbon)" }}>
                  <div className="mk-mono text-[13px] font-medium mb-5" style={{ color: "var(--mk-signal)" }}>
                    {step.n}
                  </div>
                  <h3 className="mk-display mb-3" style={{ fontSize: 18 }}>
                    {step.title}
                  </h3>
                  <p className="text-[13.5px] leading-relaxed" style={{ color: "var(--mk-muted)" }}>
                    {step.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Coach + App columns */}
        <section className="border-b" style={{ borderColor: "var(--mk-line)" }}>
          <div className="max-w-screen-xl mx-auto px-5 py-16 md:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {[
                { label: "The coach", rows: COACH_CAN },
                { label: "The app", rows: APP_HAS },
              ].map((col) => (
                <div key={col.label}>
                  <div className="flex items-center gap-4 mb-8">
                    <span className="mk-label">{col.label}</span>
                    <span className="flex-1 h-px" style={{ background: "var(--mk-line)" }} />
                  </div>
                  <div>
                    {col.rows.map(([title, body], i) => (
                      <div
                        key={i}
                        className="grid grid-cols-[40px_1fr] gap-4 py-5 border-b"
                        style={{ borderColor: "var(--mk-line)" }}
                      >
                        <span className="mk-mono text-[11px] pt-0.5" style={{ color: "var(--mk-signal)" }}>
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <div>
                          <div className="mk-display mb-1.5" style={{ fontSize: 15 }}>
                            {title}
                          </div>
                          <p className="text-[13.5px] leading-relaxed" style={{ color: "var(--mk-muted)" }}>
                            {body}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="border-b" style={{ borderColor: "var(--mk-line)" }}>
          <div className="max-w-screen-xl mx-auto px-5 py-16 md:py-24">
            <div className="flex items-center gap-4 mb-10">
              <span className="mk-label">Questions</span>
              <span className="flex-1 h-px" style={{ background: "var(--mk-line)" }} />
            </div>
            <div className="max-w-3xl">
              {FAQ.map((faq, i) => (
                <details key={i} className="group py-1">
                  <summary className="flex justify-between items-center gap-6 py-4">
                    <span className="text-[15px] font-medium" style={{ color: "var(--mk-ink)" }}>
                      {faq.q}
                    </span>
                    <span
                      className="mk-mono flex-shrink-0 group-open:rotate-45 transition-transform"
                      style={{ color: "var(--mk-signal)", fontSize: 16 }}
                    >
                      +
                    </span>
                  </summary>
                  <p className="pb-5 text-[14px] leading-relaxed max-w-2xl" style={{ color: "var(--mk-muted)" }}>
                    {faq.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section>
          <div className="max-w-screen-xl mx-auto px-5 py-20 text-center">
            <h2 className="mk-display mb-8" style={{ fontSize: "clamp(26px, 4vw, 40px)" }}>
              See what it says about your morning.
            </h2>
            <div className="flex justify-center gap-3 flex-wrap">
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="mk-btn mk-btn-solid">
                Download for iOS
              </a>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
